import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  BookOpen, 
  Calculator, 
  BarChart3, 
  TrendingUp, 
  Award,
  HelpCircle,
  CheckCircle,
  ArrowRight,
  FileText,
  Target,
  Settings,
  Users,
  Lightbulb,
  Zap
} from "lucide-react";

/**
 * MyPA User Guide Page
 * Comprehensive onboarding and help page for users
 */
export default function Guide() {
  const { t } = useTranslation();

  const steps = [
    {
      icon: Calculator,
      title: t('guide.step1_title'),
      description: t('guide.step1_desc'),
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-50 dark:bg-blue-950/30",
    },
    {
      icon: BarChart3,
      title: t('guide.step2_title'),
      description: t('guide.step2_desc'),
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-50 dark:bg-green-950/30",
    },
    {
      icon: TrendingUp,
      title: t('guide.step3_title'),
      description: t('guide.step3_desc'),
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-50 dark:bg-purple-950/30",
    },
    {
      icon: Award,
      title: t('guide.step4_title'),
      description: t('guide.step4_desc'),
      color: "text-orange-600 dark:text-orange-400",
      bgColor: "bg-orange-50 dark:bg-orange-950/30",
    },
  ];

  const features = [
    {
      title: t('guide.feature1_title'),
      description: t('guide.feature1_desc'),
      icon: Calculator,
    },
    {
      title: t('guide.feature2_title'),
      description: t('guide.feature2_desc'),
      icon: BarChart3,
    },
    {
      title: t('guide.feature3_title'),
      description: t('guide.feature3_desc'),
      icon: BookOpen,
    },
    {
      title: t('guide.feature4_title'),
      description: t('guide.feature4_desc'),
      icon: Target,
    },
    {
      title: t('guide.feature5_title'),
      description: t('guide.feature5_desc'),
      icon: FileText,
    },
    {
      title: t('guide.feature6_title'),
      description: t('guide.feature6_desc'),
      icon: Lightbulb,
    },
  ];

  const detailedFeatures = [
    {
      title: t('guide.detailed_dashboard_title'),
      description: t('guide.detailed_dashboard_desc'),
      icon: BarChart3,
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-50 dark:bg-blue-950/30",
      items: [
        t('guide.detailed_dashboard_item1'),
        t('guide.detailed_dashboard_item2'),
        t('guide.detailed_dashboard_item3'),
        t('guide.detailed_dashboard_item4'),
        t('guide.detailed_dashboard_item5'),
      ]
    },
    {
      title: t('guide.detailed_calculator_title'),
      description: t('guide.detailed_calculator_desc'),
      icon: Calculator,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-50 dark:bg-green-950/30",
      items: [
        t('guide.detailed_calculator_item1'),
        t('guide.detailed_calculator_item2'),
        t('guide.detailed_calculator_item3'),
        t('guide.detailed_calculator_item4'),
      ]
    },
    {
      title: t('guide.detailed_learn_title'),
      description: t('guide.detailed_learn_desc'),
      icon: BookOpen,
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-50 dark:bg-purple-950/30",
      items: [
        t('guide.detailed_learn_item1'),
        t('guide.detailed_learn_item2'),
        t('guide.detailed_learn_item3'),
        t('guide.detailed_learn_item4'),
        t('guide.detailed_learn_item5'),
      ]
    },
    {
      title: t('guide.detailed_benchmark_title'),
      description: t('guide.detailed_benchmark_desc'),
      icon: Target,
      color: "text-orange-600 dark:text-orange-400",
      bgColor: "bg-orange-50 dark:bg-orange-950/30",
      items: [
        t('guide.detailed_benchmark_item1'),
        t('guide.detailed_benchmark_item2'),
        t('guide.detailed_benchmark_item3'),
      ]
    },
    {
      title: t('guide.detailed_cbam_title'),
      description: t('guide.detailed_cbam_desc'),
      icon: FileText,
      color: "text-red-600 dark:text-red-400",
      bgColor: "bg-red-50 dark:bg-red-950/30",
      items: [
        t('guide.detailed_cbam_item1'),
        t('guide.detailed_cbam_item2'),
        t('guide.detailed_cbam_item3'),
      ]
    },
    {
      title: t('guide.detailed_reports_title'),
      description: t('guide.detailed_reports_desc'),
      icon: FileText,
      color: "text-cyan-600 dark:text-cyan-400",
      bgColor: "bg-cyan-50 dark:bg-cyan-950/30",
      items: [
        t('guide.detailed_reports_item1'),
        t('guide.detailed_reports_item2'),
        t('guide.detailed_reports_item3'),
      ]
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8 pb-8">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <HelpCircle className="h-8 w-8 text-primary" />
            {t('guide.title')}
          </h1>
          <p className="text-muted-foreground text-lg">
            {t('guide.subtitle')}
          </p>
        </div>

        {/* Welcome Card */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
          <CardHeader>
            <CardTitle className="text-2xl">{t('guide.welcome_title')}</CardTitle>
            <CardDescription className="text-base">
              {t('guide.welcome_desc')}
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Getting Started Steps */}
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <CheckCircle className="h-6 w-6 text-primary" />
            {t('guide.getting_started')}
          </h2>
          
          <div className="grid gap-4 md:grid-cols-2">
            {steps.map((step, index) => (
              <Card key={index} className="relative overflow-hidden">
                <div className={`absolute top-0 right-0 w-32 h-32 ${step.bgColor} rounded-bl-full opacity-50`} />
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${step.bgColor}`}>
                      <step.icon className={`h-6 w-6 ${step.color}`} />
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-muted-foreground">
                          {t('guide.step')} {index + 1}
                        </span>
                      </div>
                      <CardTitle className="text-lg">{step.title}</CardTitle>
                      <CardDescription className="text-sm leading-relaxed">
                        {step.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>

        <Separator />

        {/* Key Features Overview */}
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold">{t('guide.key_features')}</h2>
          
          <div className="grid gap-6 md:grid-cols-3">
            {features.map((feature, index) => (
              <Card key={index}>
                <CardHeader>
                  <feature.icon className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                  <CardDescription className="leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>

        <Separator />

        {/* Detailed Features */}
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Zap className="h-6 w-6 text-primary" />
            {t('guide.detailed_features_section')}
          </h2>
          
          {detailedFeatures.map((feature, index) => (
            <Card key={index} className="relative overflow-hidden">
              <div className={`absolute top-0 right-0 w-40 h-40 ${feature.bgColor} rounded-bl-full opacity-30`} />
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${feature.bgColor}`}>
                    <feature.icon className={`h-6 w-6 ${feature.color}`} />
                  </div>
                  <div className="flex-1 space-y-3">
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                    <CardDescription className="text-base leading-relaxed">
                      {feature.description}
                    </CardDescription>
                    <ul className="space-y-2 mt-4">
                      {feature.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-2 text-sm">
                          <CheckCircle className={`h-4 w-4 mt-0.5 shrink-0 ${feature.color}`} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>

        <Separator />

        {/* Understanding Your Dashboard */}
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold">{t('guide.dashboard_section')}</h2>
          
          <Card>
            <CardHeader>
              <CardTitle>{t('guide.dashboard_title')}</CardTitle>
              <CardDescription className="space-y-3 text-sm leading-relaxed">
                <p>{t('guide.dashboard_desc1')}</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <span><strong>{t('guide.dashboard_metric1')}:</strong> {t('guide.dashboard_metric1_desc')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <span><strong>{t('guide.dashboard_metric2')}:</strong> {t('guide.dashboard_metric2_desc')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <span><strong>{t('guide.dashboard_metric3')}:</strong> {t('guide.dashboard_metric3_desc')}</span>
                  </li>
                </ul>
              </CardDescription>
            </CardHeader>
          </Card>
        </div>

        <Separator />

        {/* Tips & Best Practices */}
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold">{t('guide.tips_section')}</h2>
          
          <Card className="border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20">
            <CardHeader>
              <CardTitle className="text-green-900 dark:text-green-100">
                {t('guide.tips_title')}
              </CardTitle>
              <CardContent className="px-0 space-y-2">
                <ul className="space-y-2 text-sm text-green-800 dark:text-green-200">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{t('guide.tip1')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{t('guide.tip2')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{t('guide.tip3')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{t('guide.tip4')}</span>
                  </li>
                </ul>
              </CardContent>
            </CardHeader>
          </Card>
        </div>

        {/* Help & Support */}
        <Card className="border-blue-200 dark:border-blue-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              {t('guide.help_title')}
            </CardTitle>
            <CardDescription className="text-sm leading-relaxed">
              {t('guide.help_desc')}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    </DashboardLayout>
  );
}
